//copied from https://stackoverflow.com/questions/42123407/does-typescript-support-mutually-exclusive-types
type Without<T, U> = { [P in Exclude<keyof T, keyof U>]?: never };
export type XOR<T, U> = T | U extends object
  ? (Without<T, U> & U) | (Without<U, T> & T)
  : T | U;

//copy from https://www.petermorlion.com/iterating-a-typescript-enum/
export function enumKeys<O extends object, K extends keyof O = keyof O>(
  obj: O
): K[] {
  return Object.keys(obj).filter((k) => Number.isNaN(+k)) as K[];
}

export type AtLeastOne<T> = {
  0: T;
} & Array<T>;

export type AtLeastTwo<T> = {
  0: T;
  1: T;
} & Array<T>;

export type PredictableHashingBehavior = {
  getHashCode: () => string;
};

/*
  Its a hash set with these specific behaviors:
   - collection, add, remove methods return a new set
   - uniqueness behavior is mandatory to specify
   - set can't contain undefined
*/
type Hashable = PredictableHashingBehavior | number | string;
export class JavaishSet<T extends Hashable> {
  protected state: { [key: string]: T } = {};
  constructor(fromArray?: Array<T>) {
    (fromArray || []).forEach(
      (element) => (this.state[this.valueOf(element)] = element)
    );
  }

  private valueOf(element: Hashable): string {
    if (typeof element === "string") {
      return element;
    }
    if (typeof element === "number") {
      return "" + element;
    }
    return element.getHashCode();
  }

  has(element: T): boolean {
    return this.state[this.valueOf(element)] !== undefined;
  }
  add(element: T): JavaishSet<T> {
    return new JavaishSet<T>().setState(
      Object.assign({}, this.state, {
        [this.valueOf(element)]: element,
      })
    );
  }
  remove(element: T): JavaishSet<T> {
    return new JavaishSet<T>().setState(
      Object.assign({}, this.state, {
        [this.valueOf(element)]: undefined,
      })
    );
  }
  protected setState(newState: { [key: string]: T }): JavaishSet<T> {
    this.state = newState;
    return this;
  }

  intersect(...others: AtLeastOne<JavaishSet<T>>): JavaishSet<T> {
    const valuesToInclude = Object.values(this.state)
      .filter((v) => v !== undefined)
      .filter((v) => {
        for (let i = 0; i < others.length; i++) {
          if (!others[i].has(v)) {
            return false;
          }
        }
        return true;
      });
    return new JavaishSet<T>(valuesToInclude);
  }

  union(...others: AtLeastOne<JavaishSet<T>>): JavaishSet<T> {
    const newState = Object.assign(this.state, ...others.map((o) => o.state));
    return new JavaishSet<T>().setState(newState);
  }

  subtract(...others: AtLeastOne<JavaishSet<T>>): JavaishSet<T> {
    const newValues = Object.values(this.state)
      .map((value) => {
        for (let i = 0; i < others.length; i++) {
          if (others[i].has(value)) {
            return undefined;
          }
        }
        return value;
      })
      .filter((v) => v !== undefined);

    return new JavaishSet<T>(newValues as T[]);
  }

  toArray(): Array<T> {
    return Object.values(this.state).filter((v) => v !== undefined);
  }

  equals(other: JavaishSet<T>): boolean {
    return (
      this.subtract(other).toArray().length === 0 &&
      other.subtract(this).toArray().length === 0
    );
  }
}
